import React from 'react';
import { User } from 'lucide-react';

/**
 * Reusable ProfilePicture component
 * Displays user profile picture with fallback to default avatar
 *
 * @param {string} src - Image source (base64 or URL)
 * @param {string} name - User name for alt text
 * @param {string} size - Size variant: 'xs', 'sm', 'md', 'lg', 'xl'
 * @param {string} className - Additional CSS classes
 */
const ProfilePicture = ({ src, name = 'User', size = 'md', className = '' }) => {
  // Size mapping
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20',
    '3xl': 'w-24 h-24'
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
    '2xl': 'w-10 h-10',
    '3xl': 'w-12 h-12'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const iconSize = iconSizes[size] || iconSizes.md;

  return (
    <div className={`${sizeClass} bg-gray-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${className}`}>
      {src ? (
        <img
          src={src}
          alt={`${name}'s profile`}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to default icon if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
      ) : null}
      {!src && (
        <User className={`${iconSize} text-gray-400`} />
      )}
      {/* Hidden fallback icon */}
      {src && (
        <User className={`${iconSize} text-gray-400`} style={{ display: 'none' }} />
      )}
    </div>
  );
};

export default ProfilePicture;
